import React from "react";
import { Box, IconButton, Tooltip, Divider } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import MapIcon from "@mui/icons-material/Map";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

/**
 * A single map control button.
 */
function CtrlBtn({ tooltip, icon, onClick, active, color, disabled = false }) {
  const theme = useTheme();

  const resolvedColor = active
    ? theme.palette.success.main
    : color
    ? theme.palette[color]?.main || color
    : theme.palette.text.primary;

  return (
    <Tooltip title={tooltip} placement="right" arrow>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{
            width: 34,
            height: 34,
            borderRadius: "8px",
            color: resolvedColor,
            transition: "all 0.18s ease",
            bgcolor: active ? alpha(theme.palette.success.main, 0.12) : "transparent",
            "&:hover": {
              bgcolor: alpha(resolvedColor, 0.15),
              transform: "scale(1.06)",
            },
            "&.Mui-disabled": { opacity: 0.35 },
            "& .MuiSvgIcon-root": { fontSize: 18 },
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function CtrlDivider() {
  const theme = useTheme();
  return (
    <Divider
      sx={{
        width: "70%",
        my: 0.3,
        borderColor: alpha(theme.palette.divider, 0.45),
      }}
    />
  );
}

/**
 * Left-side map control panel — Google Maps style.
 *
 * Props:
 *  onZoomIn / onZoomOut
 *  isFullscreen / onToggleFullscreen
 *  mapMode             — 'standard' | 'satellite'
 *  onToggleMapMode
 *  showLabels / onToggleLabels
 *  showArea / onToggleArea
 *  showRoute / onToggleRoute
 */
export default function MapControls({
  onZoomIn,
  onZoomOut,
  isFullscreen,
  onToggleFullscreen,
  mapMode = "standard",
  onToggleMapMode,
  showLabels,
  onToggleLabels,
  showArea,
  onToggleArea,
  showRoute,
  onToggleRoute,
}) {
  const theme = useTheme();
  const isSatelliteMode = mapMode === "satellite";

  const panelBg = isSatelliteMode
    ? alpha(theme.palette.grey[900], 0.82)
    : alpha(theme.palette.common.white, 0.9);

  return (
    <Box
      sx={{
        position: "absolute",
        top: 80,
        left: 1,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0,
        bgcolor: panelBg,
        backdropFilter: "blur(10px)",
        borderRadius: "10px",
        border: `1px solid ${alpha(
          theme.palette.divider,
          isSatelliteMode ? 0.25 : 0.18,
        )}`,
        boxShadow: `0 4px 18px ${alpha(
          theme.palette.common.black,
          isSatelliteMode ? 0.45 : 0.14,
        )}`,
        py: 0.6,
        px: 0.4,
      }}
    >
      {/* ── Zoom controls ── */}
      <CtrlBtn
        tooltip="Zoom In"
        icon={<AddIcon />}
        onClick={onZoomIn}
        color="info"
      />
      <CtrlBtn
        tooltip="Zoom Out"
        icon={<RemoveIcon />}
        onClick={onZoomOut}
        color="info"
      />

      <CtrlDivider />

      {/* ── Display mode ── */}
      {/* <CtrlBtn
        tooltip={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        icon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        onClick={onToggleFullscreen}
        color="secondary"
      />

      <CtrlDivider /> */}

      {/* ── Map mode toggle ── */}
      <CtrlBtn
        tooltip={mapMode === "standard" ? "Switch to Satellite" : "Switch to Standard Map"}
        icon={mapMode === "standard" ? <SatelliteAltIcon /> : <MapIcon />}
        onClick={onToggleMapMode}
        active={mapMode === "satellite"}
        color={mapMode === "satellite" ? "success" : "warning"}
      />

      {/* ── Labels toggle ── */}
      {/* <CtrlBtn
        tooltip={showLabels ? "Hide Labels" : "Show Labels"}
        icon={showLabels ? <VisibilityIcon /> : <VisibilityOffIcon />}
        onClick={onToggleLabels}
        active={showLabels}
        color="info"
      /> */}

      {/* <CtrlDivider /> */}

      {/* ── Layer toggles ── */}
      {/* <CtrlBtn
        tooltip={showArea ? "Hide Zones" : "Show Zones"}
        icon={<PentagonIcon />}
        onClick={onToggleArea}
        active={showArea}
        color="success"
      />
      <CtrlBtn
        tooltip={showRoute ? "Hide Routes" : "Show Routes"}
        icon={<RouteIcon />}
        onClick={onToggleRoute}
        active={showRoute}
        color="success"
      /> */}
    </Box>
  );
}
